import React, { useMemo } from "react";
import {
  Box,
  Avatar,
  Typography,
  Paper,
  Button,
  Skeleton,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  CallSplit as BranchIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { CompanyFormData } from "../types/company-types";

interface CompanyPreviewProps {
  formData: CompanyFormData;
  currentStep: number;
}

function getInitials(name: string): string {
  if (!name.trim()) return "?";
  return name
    .trim()
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

// Generates a deterministic gradient from company name for "Initials" logo mode
function nameGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h1 = Math.abs(hash) % 360;
  const h2 = (h1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${h1},60%,55%), hsl(${h2},70%,45%))`;
}

function InfoRow({
  icon,
  value,
}: {
  icon: React.ReactNode;
  value?: string;
  placeholder: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        px: 1.5,
        borderRadius: 1.5,
        bgcolor: value ? "transparent" : "action.hover",
      }}
    >
      <Box
        sx={{
          color: value ? "text.secondary" : "text.disabled",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      {value ? (
        <Typography
          variant="caption"
          sx={{ color: "text.primary", fontWeight: 500 }}
        >
          {value}
        </Typography>
      ) : (
        <Skeleton
          variant="text"
          width={120}
          height={14}
          sx={{ borderRadius: 1 }}
        />
      )}
    </Box>
  );
}

export default function CompanyPreview({
  formData,
  currentStep,
}: CompanyPreviewProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color400 = reduxTheme.primary_colors?.[400] ?? "#8B4513";
  const color900 = reduxTheme.primary_colors?.[900] ?? "#fff";

  const logoSrc = useMemo(() => {
    if (formData.logoMode === "upload" && formData.logoFile) {
      return URL.createObjectURL(formData.logoFile);
    }
    if (formData.logoMode === "url" && formData.logoUrl) {
      return formData.logoUrl;
    }
    return undefined;
  }, [formData.logoMode, formData.logoFile, formData.logoUrl]);

  const showInitials = formData.logoMode === "initials" || !logoSrc;
  const gradient = nameGradient(formData.name || "C");

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: color900,
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "120px",
          background: `linear-gradient(135deg, ${color400} 0%, #c9834a 100%)`,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 700 }}>
          Preview
        </Typography>
        <Button
          size="small"
          variant="outlined"
          sx={{
            color: "#fff",
            borderColor: "rgba(255,255,255,0.5)",
            textTransform: "none",
            fontSize: 11,
            py: 0.25,
            px: 1.5,
            minWidth: "auto",
            "&:hover": {
              borderColor: "#fff",
              bgcolor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          Preview
        </Button>
      </Box>

      {/* Body */}
      <Box
        sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column", gap: 2 }}
      >
        {/* Logo + name block */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 82,
              height: 82,
              borderRadius: "18px",
              overflow: "hidden",
              flexShrink: 0,
              background: showInitials ? gradient : undefined,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              fontWeight: 700,
              color: "#fff",
              mt: 2,
              border: "3px solid",

              ...(logoSrc && {
                backgroundImage: `url(${logoSrc})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }),
            }}
          >
            {showInitials ? getInitials(formData.name) : null}
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ lineHeight: 1.2 }}
            >
              {formData.name || "Company Name here"}
            </Typography>
            {formData.industry && (
              <Typography variant="caption" color="text.secondary">
                {formData.industry}
              </Typography>
            )}
          </Box>
          {/* Branch + Rep count pills */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <BranchIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              <Typography variant="caption" color="text.secondary">
                {formData.branches.length}{" "}
                {formData.branches.length === 1 ? "Branch" : "Branches"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PeopleIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              <Typography variant="caption" color="text.secondary">
                {formData.representatives.length}{" "}
                {formData.representatives.length === 1
                  ? "Representative"
                  : "Representatives"}
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* Contact info rows */}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <InfoRow
            icon={<EmailIcon sx={{ fontSize: 16 }} />}
            value={formData.primaryEmail}
            placeholder="Email"
          />
          <InfoRow
            icon={<PhoneIcon sx={{ fontSize: 16 }} />}
            value={formData.phone}
            placeholder="Phone"
          />
          <InfoRow
            icon={<LanguageIcon sx={{ fontSize: 16 }} />}
            value={formData.website}
            placeholder="Website"
          />
          <InfoRow
            icon={<LocationOnIcon sx={{ fontSize: 16 }} />}
            value={formData.address}
            placeholder="Address"
          />
        </Box>
        {/* Branches preview (shown on step 2+) */}
        {currentStep >= 1 && formData.branches.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
            >
              Branches
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {formData.branches.map((b) => (
                <Box
                  key={b.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: "action.hover",
                  }}
                >
                  <BranchIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                  <Typography variant="caption" fontWeight={500}>
                    {b.name || "Unnamed branch"}
                  </Typography>
                  {b.city && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ ml: "auto" }}
                    >
                      {b.city}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {/* Reps preview (shown on step 3+) */}
        {currentStep >= 2 && formData.representatives.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
            >
              Representatives
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {formData.representatives.map((r) => {
                const name =
                  [r.firstName, r.lastName].filter(Boolean).join(" ") || "?";
                return (
                  <Avatar
                    key={r.id}
                    title={name}
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: color400,
                    }}
                  >
                    {getInitials(name)}
                  </Avatar>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
